import { html, property, state } from 'lit-element'
import { ifDefined } from 'lit-html/directives/if-defined'
import { Presence, PopoverPosition } from '@getgo/chameleon-core'

import { getShellLogger } from '../../common/logger'
import type { PresenceEvents, ShellPresenceService, PresenceUpdated } from '../../services/presence'
import { PresenceServiceNamespace, convertPresenceToAppearance } from '../../services/presence'

import { getShellApiInstance } from '../../common/shell-api-helpers'
import type { PresenceProps } from './models'
import { GOTO_PRESENCE_INDICATOR } from './models'
import type { EventBus, Listener } from '../../services/namespaces'
import { t } from '../../directives/translate'
import { getAppearanceTranslationKey } from '../../common/presence-helpers'
import { ZIndex } from '../../common/z-index'
import { ShellElement } from '../../common/shell-element'

/**
 * @see react/presence-indicator/presence-indicator.shell.tsx
 */

export class GoToPresenceIndicator extends ShellElement implements PresenceProps {
  static readonly tagName = GOTO_PRESENCE_INDICATOR
  @state() private presence = Presence.OFFLINE
  @property({ type: String }) externalUserKey = ''
  @property({ type: Boolean }) presenceSnapshot = false
  @property({ type: Boolean }) achromatic = false
  @property({ type: String }) readonly tooltipPosition: PopoverPosition = 'bottom-center'
  @property({ type: Boolean }) hidePresenceTooltip = false

  connectedCallback() {
    super.connectedCallback()
    if (this.externalUserKey) {
      this.loadUserPresence()
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('externalUserKey')) {
      this.unsubscribe()
      this.loadUserPresence()
    }
  }

  private loadUserPresence() {
    const { eventBus, presence } = getShellApiInstance()
    if (this.presenceSnapshot && presence) {
      this.getPresenceSnapshot(presence)
    } else if (presence) {
      this.listenForPresenceUpdatedEvent(eventBus)
      this.subscribeToPresence(presence)
    }
  }

  private listenForPresenceUpdatedEvent(eventBus: EventBus) {
    const { presenceUpdate } = eventBus.subscribeTo<typeof PresenceServiceNamespace, typeof PresenceEvents>(
      PresenceServiceNamespace,
    )

    const presenceUpdatedHandler: Listener<PresenceUpdated> = (payload: PresenceUpdated) => {
      const { externalUserKey, presence = Presence.OFFLINE } = payload
      const canUpdate = externalUserKey === this.externalUserKey

      if (canUpdate) {
        this.presence = presence
      }
    }

    presenceUpdate.on(presenceUpdatedHandler)

    this.unsubscribeFunctions.push(() => {
      presenceUpdate.removeListener(presenceUpdatedHandler)
    })
  }

  private subscribeToPresence(presence: ShellPresenceService) {
    if (presence) {
      presence
        .subscribe(this.externalUserKey)
        .then(() => {
          // https://jira.ops.expertcity.com/browse/SCAPI-46?focusedCommentId=205142
          this.unsubscribeFunctions.push(() => {
            presence.unsubscribe(this.externalUserKey)
          })
        })
        .catch(() => {
          getShellLogger().error(`failed to subscribe to presence for external user key ${this.externalUserKey}`)
        })
    }
  }

  private getPresenceSnapshot(presenceService: ShellPresenceService) {
    if (!presenceService) {
      this.presence = Presence.OFFLINE
      return
    }

    return presenceService.getPresenceSnapshots(this.externalUserKey).then(presenceSnapshot => {
      this.presence = presenceSnapshot.presence ?? Presence.OFFLINE
    })
  }

  private getPresenceIndicatorTemplate(slot?: string) {
    return html`<chameleon-presence-indicator
      slot=${ifDefined(slot)}
      .presence=${this.presence}
      ?achromatic=${this.achromatic}
    ></chameleon-presence-indicator>`
  }

  private getChameleonTooltipTemplate() {
    return html`<chameleon-tooltip-v2 position="${this.tooltipPosition}" z-index="${ZIndex.tooltip}"
      >${this.getPresenceIndicatorTemplate('trigger')}
      <span slot="content">${t(getAppearanceTranslationKey(convertPresenceToAppearance(this.presence), false))}</span>
    </chameleon-tooltip-v2>`
  }

  render() {
    return html`${this.hidePresenceTooltip ? this.getPresenceIndicatorTemplate() : this.getChameleonTooltipTemplate()}`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToPresenceIndicator.tagName]: GoToPresenceIndicator
  }
}
